import {autoRegister, resolve} from "../../container";
import {type ChatResponse, GptChatService} from "./gptChatService";
import {SessionStorage} from "../../common/clientStorage";
import {Dictionary} from "../../page/elements/dictionary";
import {schedule} from "../../common/utils/promises";
import {type Message, Role} from "./careergpt";
import type {Maybe} from "../../bootstrap/common/types";
import {LanguagesService} from "../../common/languages";

const JOB_URL_MARKDOWN_PATTERN = /\[([^[\]]+)]\((https?:\/\/[^\s)]+)\)/g; //example: [click me](https://www.example.com) <- label and link are in a group each without parenthesis
const JOB_HIGHLIGHT_MARKDOWN_PATTERN = /(\*{2}).+?(\*{2})/g; //example: **I am bold** <- highlighted text is in a group
const JOB_HEADLINE_MARKDOWN_PATTERN = /^#+ /gm;
const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,64}\.[a-zA-Z]{2,63})/g;

const processMarkdownLinks: MessageProcessor = (message: string) => {
    const markdownRefs = message.matchAll(JOB_URL_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownRef of markdownRefs) {
        const clickLabel = markdownRef[1];
        const plainRef = markdownRef[2];

        const link = `<a href="${plainRef}" target="_blank" class="link common-link"
            data-tracking-event-name="textlink_interact"
            data-tracking-payload-text="${clickLabel}"
            data-tracking-payload-link="${plainRef}"
        >${clickLabel}</a>`;
        processedMessage = processedMessage.replace(markdownRef[0], link);
    }
    return processedMessage;
};

const processATags: MessageProcessor = (message: string) => {
    const messageElement = document.createElement("div");
    messageElement.innerHTML = message;

    const links = messageElement.querySelectorAll("a");
    links.forEach((link) => {
        link.classList.add("link", "common-link");
        link.target = "_blank";
    });

    return messageElement.innerHTML;
};

const processMarkdownHighlighting: MessageProcessor = (message: string) => {
    const markdownHighlights = message.matchAll(JOB_HIGHLIGHT_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHighlight of markdownHighlights) {
        processedMessage = processedMessage.replace(markdownHighlight[1], "<b>");
        processedMessage = processedMessage.replace(markdownHighlight[2], "</b>");
    }
    return processedMessage;
};

const processMarkdownHeadline: MessageProcessor = (message: string) => {
    const markdownHeadlines = message.matchAll(JOB_HEADLINE_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHeadline of markdownHeadlines) {
        processedMessage = processedMessage.replace(markdownHeadline[0], "");
    }
    return processedMessage;
};

const processPlaintextEmails: MessageProcessor = (message: string) => {
    return message.replaceAll(EMAIL_PATTERN, (match) => {
        const isAlreadyLink = message.includes(`href="mailto:${match}"`);
        if (isAlreadyLink) {
            return match;
        }
        return `<a href="mailto:${match}" class="link common-link"
            data-tracking-event-name="textlink_interact"
            data-tracking-payload-text="${match}"
            data-tracking-payload-link="mailto:${match}"
        >${match}</a>`;
    });
};

class CompoundMessageProcessor {
    private processors: MessageProcessor[] = [];

    public addProcessor(processor: MessageProcessor): this {
        this.processors.push(processor);
        return this;
    }

    public process(message: string): string {
        let processedMessage = message;
        this.processors.forEach((processorCallback) => {
            processedMessage = processorCallback(processedMessage);
        });
        return processedMessage;
    }
}

type StoredHistory = { history: string }

type MessageProcessor = (message: string) => string;

type NewMessagesCallback = (messages: Message[]) => void;

@autoRegister()
export class GptChatAppFactory {

    public constructor(
        private gptChatService: GptChatService = resolve(GptChatService),
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private languages: LanguagesService = resolve(LanguagesService)
    ) {
    }

    public create(configId: string, traceId: string, dictionary: Dictionary): GptChatApp {
        return new GptChatApp(this.gptChatService, this.sessionStorage, this.languages, configId, traceId, dictionary);
    }
}

export class GptChatApp {

    private messageProcessor = new CompoundMessageProcessor()
        .addProcessor(processPlaintextEmails)
        .addProcessor(processMarkdownLinks)
        .addProcessor(processATags)
        .addProcessor(processMarkdownHighlighting)
        .addProcessor(processMarkdownHeadline);

    private chatHistoryStorageKey: string;
    private chatCommunicationStorageKey: string;

    private newMessagesCallback: NewMessagesCallback[] = [];

    public constructor(
        private gptChatService: GptChatService,
        private sessionStorage: SessionStorage,
        private languages: LanguagesService,
        private configId: string,
        private traceId: string,
        private dictionary: Dictionary
    ) {
        this.chatHistoryStorageKey = "chat-history-" + this.configId + "-" + this.languages.activeLanguageId();
        this.chatCommunicationStorageKey = "chat-communication-" + this.configId + "-" + this.languages.activeLanguageId();
    }

    public onNewMessages(callback: NewMessagesCallback): void {
        this.newMessagesCallback.push(callback);
    }

    public async chat(userInput: string): Promise<void> {
        let messages = this.saveMessage({content: userInput, role: Role.USER});
        this.fireNewMessages(messages);

        const responseMessage = await this.post(userInput);

        messages = this.saveMessage({content: responseMessage, role: Role.ASSISTANT});
        this.fireNewMessages(messages);
    }

    private async post(userInput: string): Promise<string> {
        try {
            const history = this.sessionStorage.fetch<StoredHistory>(this.chatHistoryStorageKey)?.history;
            const response = await this.postAndReceiveMessage(userInput, this.traceId, history);
            this.sessionStorage.save(this.chatHistoryStorageKey, {history: response.history});
            return this.postProcessMessage(response.message, response.responseType);
        } catch (_) {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        }
    }

    private saveMessage(message: Message): Message[] {
        const stored = this.sessionStorage.fetch<Message[]>(this.chatCommunicationStorageKey) ?? [];
        const messages = [...stored, message];
        this.sessionStorage.save(this.chatCommunicationStorageKey, messages);
        return messages;
    }

    private async postAndReceiveMessage(userMessage: string, traceId: string, history: Maybe<string>): Promise<ChatResponse> {
        return await schedule(this.gptChatService.postMessage(
            this.configId,
            traceId,
            userMessage,
            history,
            this.languages.activeLanguageId()
        )).as("post-message");
    }

    private postProcessMessage(message: string, responseType: string): string {
        if (responseType === "success") {
            return this.messageProcessor.process(message);
        } else if (responseType === "content_too_long") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_TOO_LONG");
        } else if (responseType === "content_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_VIOLATION");
        } else if (responseType === "bad_conduct_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_BAD_CONDUCT");
        } else if (responseType === "company_policy_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_COMPANY_POLICY");
        } else if (responseType === "internal_server_error") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        } else {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        }
    }

    public init(welcomeMessage: string): void {
        const oldMessages = this.sessionStorage.fetch<Message[]>(this.chatCommunicationStorageKey) ?? [];
        let messages: Message[];
        if (oldMessages.length === 0 && welcomeMessage !== "") {
            messages = this.saveMessage({content: welcomeMessage, role: Role.ASSISTANT});
        } else {
            messages = oldMessages;
        }
        this.fireNewMessages(messages);
    }

    private fireNewMessages(messages: Message[]): void {
        this.newMessagesCallback.forEach(callback => callback(messages));
    }
}